import { useCallback, useState } from 'react';
import { toast } from 'sonner';

/**
 * useDownloadFile Hook
 * 
 * 프로젝트 전반에서 파일을 안전하게 다운로드하기 위한 공통 훅입니다.
 * - Supabase Storage CORS 문제를 방지하기 위해 credentials를 포함하지 않는 fetch를 사용합니다.
 * - Blob 처리를 통해 브라우저 다운로드를 트리거합니다.
 * - 다운로드 중 로딩 상태를 관리합니다.
 */
export function useDownloadFile() {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadFile = useCallback(async (url: string, fileName: string) => {
    if (!url) {
      toast.error('다운로드할 파일 주소가 없습니다.');
      return;
    }

    try {
      setIsDownloading(true);

      /**
       * ✅ Supabase Storage CORS Fix
       * Signed URL은 이미 권한 정보를 포함하고 있습니다.
       * 브라우저 쿠키나 세션 헤더가 포함되면 Storage 서버의 CORS 설정과 충돌합니다.
       * 'credentials: omit'을 사용하여 순수하게 파일만 요청합니다.
       */
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'omit', // 쿠키 및 인증 헤더 제외
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('[useDownloadFile] Download failed:', error);
      toast.error('파일 다운로드 중 오류가 발생했습니다.');
    } finally {
      setIsDownloading(false);
    }
  }, []);

  return {
    downloadFile,
    isDownloading,
  };
}
